import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../App';
import translations from '../translations';
import { collectKeys, lookup } from '../i18n/translate';
import './AdminTranslations.css';

// Rows are derived from the bundled defaults, so the editor's key list can
// never drift from the real key set in code.
const ALL_KEYS = collectKeys(translations.sl);

const groupOf = (key) => key.split('.')[0];
const LONG_TEXT_THRESHOLD = 80;

function AdminTranslations() {
  const { adminFetch } = useAuth();

  // Saved overrides from the DB, keyed by translation key.
  const [overrides, setOverrides] = useState({});
  // Local unsaved edits, keyed by translation key: { sl, en }
  const [edits, setEdits] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);
  const [search, setSearch] = useState('');
  // "Untranslated" = the English value is still identical to the Slovenian
  // one. Because every key ships with a bundled English default, a key is
  // never truly empty; an SL==EN pair is the real "needs a distinct
  // translation" signal (proper nouns like the app name are expected matches).
  const [onlyUntranslated, setOnlyUntranslated] = useState(false);
  const [collapsed, setCollapsed] = useState({});

  useEffect(() => {
    loadOverrides();
  }, []);

  const loadOverrides = async () => {
    setLoading(true);
    try {
      const res = await adminFetch('/api/admin/translations');
      const data = await res.json();
      if (res.ok) {
        const map = {};
        for (const row of data.overrides || []) {
          map[row.key] = { sl: row.sl || '', en: row.en || '' };
        }
        setOverrides(map);
        setEdits({});
      } else {
        setStatus({ type: 'error', message: 'Nalaganje prevodov ni uspelo' });
      }
    } catch (err) {
      setStatus({ type: 'error', message: 'Nalaganje prevodov ni uspelo' });
    } finally {
      setLoading(false);
    }
  };

  // Current displayed value: unsaved edit → saved override → bundled default.
  const valueFor = (key, lang) => {
    if (edits[key] && edits[key][lang] !== undefined) return edits[key][lang];
    if (overrides[key] && overrides[key][lang]) return overrides[key][lang];
    return lookup(translations[lang], key) || '';
  };

  const defaultFor = (key, lang) => lookup(translations[lang], key) || '';

  const isOverridden = (key, lang) =>
    Boolean(overrides[key] && overrides[key][lang]) ||
    (edits[key] && edits[key][lang] !== undefined &&
      edits[key][lang] !== defaultFor(key, lang));

  const setValue = (key, lang, value) => {
    setEdits((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), [lang]: value },
    }));
  };

  const revert = (key) => {
    // Sending empty strings for both languages deletes the row server-side.
    setEdits((prev) => ({ ...prev, [key]: { sl: '', en: '' } }));
  };

  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();

    return ALL_KEYS.filter((key) => {
      if (onlyUntranslated) {
        const sl = valueFor(key, 'sl').trim();
        const en = valueFor(key, 'en').trim();
        if (sl !== en) return false;
      }
      if (!needle) return true;
      return (
        key.toLowerCase().includes(needle) ||
        valueFor(key, 'sl').toLowerCase().includes(needle) ||
        valueFor(key, 'en').toLowerCase().includes(needle)
      );
    });
  }, [search, onlyUntranslated, overrides, edits]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const key of rows) {
      const group = groupOf(key);
      if (!map.has(group)) map.set(group, []);
      map.get(group).push(key);
    }
    return map;
  }, [rows]);

  const dirtyCount = Object.keys(edits).length;

  const save = async () => {
    if (!dirtyCount) return;
    setSaving(true);
    setStatus(null);

    // Send only keys the admin actually touched. A value equal to the
    // bundled default is sent as '' so the row is removed rather than
    // storing a redundant override.
    const entries = Object.entries(edits).map(([key, value]) => {
      const sl = value.sl !== undefined ? value.sl : (overrides[key]?.sl || '');
      const en = value.en !== undefined ? value.en : (overrides[key]?.en || '');
      return {
        key,
        sl: sl === defaultFor(key, 'sl') ? '' : sl,
        en: en === defaultFor(key, 'en') ? '' : en,
      };
    });

    try {
      const res = await adminFetch('/api/admin/translations', {
        method: 'PUT',
        body: JSON.stringify({ entries }),
      });
      const data = await res.json();

      if (res.ok) {
        setStatus({
          type: 'success',
          message: `Shranjeno: ${data.saved} · Ponastavljeno: ${data.deleted}`,
        });
        await loadOverrides();
      } else {
        setStatus({ type: 'error', message: data.error || 'Shranjevanje ni uspelo' });
      }
    } catch (err) {
      setStatus({ type: 'error', message: 'Shranjevanje ni uspelo' });
    } finally {
      setSaving(false);
    }
  };

  const toggleGroup = (group) => {
    setCollapsed((prev) => ({ ...prev, [group]: !prev[group] }));
  };

  if (loading) {
    return <div className="admin-translations"><p>Nalaganje prevodov...</p></div>;
  }

  return (
    <div className="admin-translations">
      <div className="translations-toolbar">
        <input
          type="search"
          className="translations-search"
          placeholder="Iskanje po ključu ali besedilu..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <label className="translations-filter">
          <input
            type="checkbox"
            checked={onlyUntranslated}
            onChange={(e) => setOnlyUntranslated(e.target.checked)}
          />
          Samo neprevedeni (SL = EN)
        </label>
        <span className="translations-count">{rows.length} ključev</span>
        <button
          className="btn btn-primary"
          onClick={save}
          disabled={saving || !dirtyCount}
        >
          {saving ? 'Shranjevanje...' : `Shrani spremembe${dirtyCount ? ` (${dirtyCount})` : ''}`}
        </button>
      </div>

      {status && (
        <div className={`translations-status ${status.type}`}>{status.message}</div>
      )}

      {[...grouped.entries()].map(([group, keys]) => (
        <section key={group} className="translations-group">
          <button className="translations-group-header" onClick={() => toggleGroup(group)}>
            <span className="chevron">{collapsed[group] ? '▸' : '▾'}</span>
            {group} <span className="group-count">({keys.length})</span>
          </button>

          {!collapsed[group] && (
            <table className="translations-table">
              <thead>
                <tr>
                  <th className="col-key">Ključ</th>
                  <th>Slovenščina</th>
                  <th>English</th>
                  <th className="col-actions"></th>
                </tr>
              </thead>
              <tbody>
                {keys.map((key) => {
                  const slValue = valueFor(key, 'sl');
                  const enValue = valueFor(key, 'en');
                  const isLong =
                    slValue.length > LONG_TEXT_THRESHOLD ||
                    enValue.length > LONG_TEXT_THRESHOLD;
                  const Field = isLong ? 'textarea' : 'input';
                  const modified = isOverridden(key, 'sl') || isOverridden(key, 'en');

                  return (
                    <tr key={key} className={modified ? 'modified' : ''}>
                      <td className="col-key">
                        <code>{key}</code>
                        {modified && <span className="modified-badge" title="Spremenjeno">●</span>}
                      </td>
                      <td>
                        <Field
                          value={slValue}
                          onChange={(e) => setValue(key, 'sl', e.target.value)}
                        />
                      </td>
                      <td>
                        <Field
                          className={enValue.trim() === '' ? 'missing' : ''}
                          value={enValue}
                          onChange={(e) => setValue(key, 'en', e.target.value)}
                        />
                      </td>
                      <td className="col-actions">
                        {modified && (
                          <button
                            className="revert-btn"
                            title="Ponastavi na privzeto"
                            onClick={() => revert(key)}
                          >
                            ↺
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </section>
      ))}
    </div>
  );
}

export default AdminTranslations;
